import { type ReactElement, Suspense } from 'react'
import { NotReady, CompanyInfo, PrefetchedUserInfo, RelativeDateWithTooltip, TButton, Page } from '@components'
import { useVideo, useProfile, useUpdateVideo, useVideoReportReasons, useTranslation } from '@front/model'
import { useToastError } from '@hooks'
import { Stack, Group, Input, Loader, Grid, Divider, ScrollArea } from '@mantine/core'
import { useNavigate, useRouter, createFileRoute } from '@tanstack/react-router'
import { type WithUserId, type WithVideoId, isEmpty } from '@terros/common'
import { useRequiredParams } from '@util'
import HlsPlayer from './HlsPlayer'

function ShortsReportsPlayerPage(): ReactElement {
  const { t } = useTranslation()
  const { videoId } = useRequiredParams<WithVideoId>()
  const { data, isLoading, error, refetch, isFetching } = useVideo(videoId)
  const { update, isUpdating, error: updateError } = useUpdateVideo()
  useToastError(updateError)
  const router = useRouter()
  const navigate = useNavigate()

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='Video' isLoading={isLoading} />

  const { video } = data

  const approve = async (): Promise<void> => {
    await update({ video: { ...video, reviewedAt: Date.now() } })
    router.history.back()
  }
  const remove = async (): Promise<void> => {
    await update({ video: { ...video, status: 'Removed' } })
    router.history.back()
  }

  return (
    <Page>
      <Page.Heading back isFetching={isFetching} refetch={refetch} title={t('Reported Short')} />
      <Page.Content>
        <Suspense fallback={<NotReady isLoading />}>
          <Grid>
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <Stack gap='xs'>
                <HlsPlayer
                  autoPlay
                  controls
                  loop
                  playsInline
                  className='relative z-10 aspect-video w-full rounded-lg object-contain'
                  manifest={video.playlistUrl}
                  poster={video.thumbnailUrl}
                  style={{ aspectRatio: 9 / 16, flex: 1, maxHeight: '70vh' }}
                />
                <Group gap='xs'>
                  <TButton className='flex-1' color='red' icon='close' loading={isUpdating} onClick={remove}>
                    {t('Reject')}
                  </TButton>
                  <TButton className='flex-1' color='green' icon='check' loading={isUpdating} onClick={approve}>
                    {t('Approve')}
                  </TButton>
                </Group>
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <Stack>
                <Group>
                  <Stack flex={1}>
                    <Input.Label>{t('Owner')}</Input.Label>
                    <PrefetchedUserInfo user={video.owner} />
                  </Stack>
                  <Divider orientation='vertical' />
                  <Stack flex={1}>
                    <Input.Label>{t('Company')}</Input.Label>
                    <CompanyInfoFromProfile userId={video.ownerId} />
                  </Stack>
                </Group>
                <Group justify='space-evenly'>
                  <Indicator label={t('Views')} value={video.viewCount} />
                  <Divider orientation='vertical' />
                  <Indicator label={t('Likes')} value={video.likeCount} />
                  <Divider orientation='vertical' />
                  <Indicator label={t('Reports')} value={video.reportCount} />
                  <Divider orientation='vertical' />
                  <Stack className='text-center' gap='xs'>
                    <Input.Label>{t('Published')}</Input.Label>
                    {video.publishedAt && <RelativeDateWithTooltip description date={video.publishedAt} />}
                  </Stack>
                </Group>
                <Stack gap={0}>
                  <Input.Label>{t('Description')}</Input.Label>
                  <Input.Description>{video.description}</Input.Description>
                </Stack>
                <ReportReasons videoId={videoId} />
              </Stack>
            </Grid.Col>
          </Grid>
        </Suspense>
      </Page.Content>
    </Page>
  )
}

const ReportReasons = ({ videoId }: WithVideoId): ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading, error } = useVideoReportReasons(videoId)
  if (isLoading) return <Loader size='sm' />
  if (error || !data) return <div>{t('Error')}</div>

  return (
    <Stack gap={0}>
      <Input.Label>{t('Reports')}</Input.Label>
      {isEmpty(data) && <Input.Description>{t('No reports found')}</Input.Description>}
      <ScrollArea h='40vh'>
        {data.map(({ user, reason, lastViewedAt }) => (
          <Group gap='xs' key={user.userId}>
            <PrefetchedUserInfo user={user} />
            <Divider orientation='vertical' />
            <Input.Description>{reason}</Input.Description>
            <Divider orientation='vertical' />
            {lastViewedAt && <RelativeDateWithTooltip description date={lastViewedAt} />}
          </Group>
        ))}
      </ScrollArea>
    </Stack>
  )
}

const Indicator = ({ label, value }: { label: string; value: string | number }): ReactElement => {
  return (
    <Stack className='text-center' gap='xs'>
      <Input.Label>{label}</Input.Label>
      <Input.Description>{typeof value === 'number' ? value.toLocaleString() : value}</Input.Description>
    </Stack>
  )
}

const CompanyInfoFromProfile = ({ userId }: WithUserId): ReactElement => {
  const { t } = useTranslation()
  const { company, isLoading, error } = useProfile(userId)

  if (isLoading) return <Loader size='sm' />
  if (error || !company) return <div>{t('Error')}</div>

  return <CompanyInfo companyId={company.companyId} />
}

export const Route = createFileRoute('/short/reports_/$videoId/')({
  component: ShortsReportsPlayerPage,
})
